import { z } from 'zod'
import { pageQuerySchema, versionSchema } from './common.js'

/**
 * The privacy engine minimum (01 §10, ADR-025): data classes with legal floors, retention rules,
 * legal holds, anonymisation through module adapters, and a staff-logged queue of data-subject
 * requests (DSR) with a 45-calendar-day clock.
 */

export const RETENTION_ACTIONS = ['ANONYMISE', 'PURGE_FILES'] as const
export type RetentionAction = (typeof RETENTION_ACTIONS)[number]

/** What starts a subject's retention period. */
export const RETENTION_TRIGGERS = ['ARCHIVED', 'DEACTIVATED'] as const
export type RetentionTrigger = (typeof RETENTION_TRIGGERS)[number]

export interface DataClassDef {
  en: string
  ar: string
  /** Why the company may hold the data (shown in the processing register). */
  legalBasis: { en: string; ar: string }
  trigger: RetentionTrigger
  /** The shortest retention period allowed, in months after the trigger (NEW ASM, R0b X2). */
  floorMonths: number
  actions: readonly RetentionAction[]
  /** Whether a module adapter can locate, export and anonymise it; a rule without one cannot be enabled. */
  hasAdapter: boolean
}

/** Recruitment classes arrive in R1/R2. */
export const DATA_CLASSES = {
  customer: {
    en: 'Customers',
    ar: 'العملاء',
    legalBasis: {
      en: 'Contract and legal obligation (tax and accounting records)',
      ar: 'العقد والالتزام القانوني (السجلات الضريبية والمحاسبية)',
    },
    trigger: 'ARCHIVED',
    floorMonths: 12,
    actions: ['ANONYMISE'],
    hasAdapter: true,
  },
  user: {
    en: 'Users',
    ar: 'المستخدمون',
    legalBasis: {
      en: 'Legitimate interest (account security and the audit trail)',
      ar: 'المصلحة المشروعة (أمن الحسابات وسجل التدقيق)',
    },
    trigger: 'DEACTIVATED',
    floorMonths: 12,
    actions: ['ANONYMISE'],
    hasAdapter: false,
  },
  /**
   * Projects P6: a user's project time and the project notes they wrote. Anonymising clears the notes;
   * the hours and amounts stay (accounting records).
   */
  project_time: {
    en: 'Project time and notes',
    ar: 'وقت المشاريع وملاحظاتها',
    legalBasis: {
      en: 'Contract and legal obligation (project costing and client billing records)',
      ar: 'العقد والالتزام القانوني (سجلات تكاليف المشاريع وفوترة العملاء)',
    },
    trigger: 'DEACTIVATED',
    floorMonths: 12,
    actions: ['ANONYMISE'],
    hasAdapter: true,
  },
  /**
   * P8: a user's assistant conversations (questions, answers, paused runs). Text is also purged by the
   * company's `retentionDays` (Settings › AI assistant); run metadata is kept (ASM-218).
   */
  ai_conversation: {
    en: 'AI assistant conversations',
    ar: 'محادثات المساعد الذكي',
    legalBasis: {
      en: 'Consent of the company that enabled the assistant (its own account), for the user who asked',
      ar: 'موافقة الشركة التي فعّلت المساعد (عبر حسابها الخاص) لصالح المستخدم الذي سأل',
    },
    trigger: 'DEACTIVATED',
    floorMonths: 1,
    actions: ['ANONYMISE'],
    hasAdapter: true,
  },
} as const satisfies Record<string, DataClassDef>

export type DataClass = keyof typeof DATA_CLASSES
export const DATA_CLASS_KEYS = Object.keys(DATA_CLASSES) as DataClass[]

export function isDataClass(value: string): value is DataClass {
  return Object.hasOwn(DATA_CLASSES, value)
}

// ── retention rules ──────────────────────────────────────────────────────────

/**
 * PUT /privacy/retention-rules: a versioned upsert of one class's rule (a class never saved reads as
 * off at version 1). The period may not go below the class's floor (422 RETENTION_BELOW_FLOOR in the API).
 */
export const retentionRuleSchema = z
  .object({
    dataClass: z.enum(DATA_CLASS_KEYS as [DataClass, ...DataClass[]]),
    enabled: z.boolean(),
    trigger: z.enum(RETENTION_TRIGGERS),
    periodMonths: z.number().int().min(1).max(1200),
    action: z.enum(RETENTION_ACTIONS),
    version: versionSchema,
  })
  .strict()
  .superRefine((rule, ctx) => {
    const def: DataClassDef = DATA_CLASSES[rule.dataClass]
    if (rule.periodMonths < def.floorMonths) {
      ctx.addIssue({
        code: 'custom',
        path: ['periodMonths'],
        message: `At least ${def.floorMonths} months`,
      })
    }
    if (!def.actions.includes(rule.action)) {
      ctx.addIssue({ code: 'custom', path: ['action'], message: 'Not allowed for this data class' })
    }
    if (rule.trigger !== def.trigger) {
      ctx.addIssue({ code: 'custom', path: ['trigger'], message: 'Not the trigger of this data class' })
    }
    if (rule.enabled && !def.hasAdapter) {
      ctx.addIssue({ code: 'custom', path: ['enabled'], message: 'This data class cannot be retained yet' })
    }
  })
export type RetentionRuleInput = z.input<typeof retentionRuleSchema>

export interface RetentionRule {
  dataClass: DataClass
  enabled: boolean
  trigger: RetentionTrigger
  periodMonths: number
  action: RetentionAction
  version: number
  updatedAt: string | null
  updatedBy: string | null
}

/** One run of `privacy.retention` for a class. */
export interface PrivacyRun {
  id: string
  dataClass: DataClass
  startedAt: string
  finishedAt: string | null
  due: number
  enqueued: number
  heldSkipped: number
}

/** GET /privacy/processing-register: one row per data class (read only). */
export interface ProcessingRegisterEntry {
  dataClass: DataClass
  label: { en: string; ar: string }
  legalBasis: { en: string; ar: string }
  floorMonths: number
  hasAdapter: boolean
  rule: RetentionRule | null
}

// ── legal holds ──────────────────────────────────────────────────────────────

export const LEGAL_HOLD_SUBJECT_TYPES = ['customer', 'user'] as const
export type LegalHoldSubjectType = (typeof LEGAL_HOLD_SUBJECT_TYPES)[number]

/** POST /privacy/legal-holds: one active hold per subject; a held subject is never anonymised. */
export const legalHoldSchema = z
  .object({
    subjectType: z.enum(LEGAL_HOLD_SUBJECT_TYPES),
    subjectId: z.uuid(),
    reason: z.string().trim().min(10).max(500),
  })
  .strict()
export type LegalHoldInput = z.input<typeof legalHoldSchema>

/**
 * POST /privacy/legal-holds/:id/lift: why the hold ends (kept in the audit trail, as personal data).
 * Optional for API callers; the settings screen always asks for it.
 */
export const legalHoldLiftSchema = z
  .object({ reason: z.string().trim().min(10).max(500).optional() })
  .strict()
export type LegalHoldLiftInput = z.input<typeof legalHoldLiftSchema>

export const legalHoldListQuerySchema = pageQuerySchema.extend({
  active: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  subjectType: z.enum(LEGAL_HOLD_SUBJECT_TYPES).optional(),
})
export type LegalHoldListQuery = z.input<typeof legalHoldListQuerySchema>

export interface LegalHold {
  id: string
  subjectType: LegalHoldSubjectType
  subjectId: string
  /** What the subject is called today (null once anonymised or gone). */
  subjectLabel: string | null
  reason: string
  placedAt: string
  placedBy: { id: string; name: string } | null
  liftedAt: string | null
  liftedBy: { id: string; name: string } | null
}

// ── audit redaction ──────────────────────────────────────────────────────────

/** What an audit change value reads as once the personal value behind its token was removed. */
export interface RedactedValue {
  $redacted: true
}
export const REDACTED: RedactedValue = Object.freeze({ $redacted: true }) as RedactedValue

export function isRedacted(value: unknown): value is RedactedValue {
  return typeof value === 'object' && value !== null && (value as { $redacted?: unknown }).$redacted === true
}

// ── data-subject requests (DSR) ──────────────────────────────────────────────

export const DSR_KINDS = [
  'ACCESS',
  'CORRECTION',
  'ERASURE',
  'OBJECTION',
  'PORTABILITY',
  'CONSENT_WITHDRAWAL',
] as const
export type DsrKind = (typeof DSR_KINDS)[number]

export const DSR_STATUSES = ['RECEIVED', 'VERIFIED', 'IN_PROGRESS', 'COMPLETED', 'REFUSED'] as const
export type DsrStatus = (typeof DSR_STATUSES)[number]

/** How the request reached the company (staff log it; no self-service in R0b). */
export const DSR_CHANNELS = ['EMAIL', 'LETTER', 'IN_PERSON', 'PHONE', 'OTHER'] as const
export type DsrChannel = (typeof DSR_CHANNELS)[number]

/** Legal edges; COMPLETED and REFUSED are final. */
export const DSR_TRANSITIONS: Record<DsrStatus, readonly DsrStatus[]> = {
  RECEIVED: ['VERIFIED', 'REFUSED'],
  VERIFIED: ['IN_PROGRESS', 'REFUSED'],
  IN_PROGRESS: ['COMPLETED', 'REFUSED'],
  COMPLETED: [],
  REFUSED: [],
}

export function canDsrTransition(from: DsrStatus, to: DsrStatus): boolean {
  return DSR_TRANSITIONS[from].includes(to)
}

/** Calendar days the company has to answer (01 §10). */
export const DSR_LEGAL_DAYS = 45

/** Working days before `due_on` on which the open request's assignee is reminded (job `privacy.dsr_reminders`). */
export const DSR_REMINDER_WORKING_DAYS = [10, 5, 2] as const

const DAY_MS = 86_400_000
const isoDay = (date: string) => Date.parse(`${date}T00:00:00Z`)

/**
 * The DSR clock on business dates (YYYY-MM-DD): the received day is day 1; on `dueOn` (received + 45)
 * it shows day 45 with 0 days left; the day after is overdue (negative days left).
 */
export function dsrClock(
  receivedOn: string,
  today: string,
): { dueOn: string; day: number; daysLeft: number; overdue: boolean } {
  const received = isoDay(receivedOn)
  const now = isoDay(today)
  if (Number.isNaN(received) || Number.isNaN(now)) throw new RangeError('Invalid date')
  const due = received + DSR_LEGAL_DAYS * DAY_MS
  const elapsed = Math.round((now - received) / DAY_MS)
  return {
    dueOn: new Date(due).toISOString().slice(0, 10),
    day: Math.min(Math.max(elapsed + 1, 1), DSR_LEGAL_DAYS),
    daysLeft: Math.round((due - now) / DAY_MS),
    overdue: now > due,
  }
}

/** POST /privacy/dsr: `receivedOn` may be back-dated, never in the future (checked by the API). */
export const dsrCreateSchema = z
  .object({
    kind: z.enum(DSR_KINDS),
    requesterName: z.string().trim().min(1).max(200),
    requesterContact: z.string().trim().min(1).max(254),
    channel: z.enum(DSR_CHANNELS),
    receivedOn: z.iso.date(),
    /** Known at logging time, or set at verification. */
    subjectType: z.enum(LEGAL_HOLD_SUBJECT_TYPES).nullish(),
    subjectId: z.uuid().nullish(),
    details: z.string().trim().max(2000).nullish(),
    assigneeId: z.uuid().nullish(),
  })
  .strict()
  .refine((v) => !v.subjectType === !v.subjectId, {
    path: ['subjectId'],
    message: 'Give both the subject type and the subject, or neither',
  })
export type DsrCreate = z.input<typeof dsrCreateSchema>

/** POST /privacy/dsr/:id/verify: the subject is confirmed (required from here on). */
export const dsrVerifySchema = z
  .object({
    subjectType: z.enum(LEGAL_HOLD_SUBJECT_TYPES),
    subjectId: z.uuid(),
    version: versionSchema,
  })
  .strict()
export type DsrVerify = z.input<typeof dsrVerifySchema>

/** POST /privacy/dsr/:id/start and …/complete. */
export const dsrActionSchema = z.object({ version: versionSchema }).strict()
export type DsrAction = z.input<typeof dsrActionSchema>

/** POST /privacy/dsr/:id/refuse: the reason is kept and told to the requester. */
export const dsrRefuseSchema = z
  .object({ reason: z.string().trim().min(3).max(1000), version: versionSchema })
  .strict()
export type DsrRefuse = z.input<typeof dsrRefuseSchema>

export const DSR_SORT_FIELDS = ['receivedOn', 'dueOn', 'status', 'createdAt'] as const
export const dsrListQuerySchema = pageQuerySchema.extend({
  status: z.enum(DSR_STATUSES).optional(),
  kind: z.enum(DSR_KINDS).optional(),
  /** Open requests only (not COMPLETED or REFUSED). */
  open: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  overdue: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  /** Open requests due within this many days from today, not yet overdue ("Due in 7 days"). */
  dueWithin: z.coerce.number().int().min(0).max(DSR_LEGAL_DAYS).optional(),
})
export type DsrListQuery = z.input<typeof dsrListQuerySchema>

export interface DsrEvent {
  id: string
  at: string
  by: { id: string; name: string } | null
  fromStatus: DsrStatus | null
  toStatus: DsrStatus
  note: string | null
}

export interface DsrRequest {
  id: string
  kind: DsrKind
  status: DsrStatus
  requesterName: string
  requesterContact: string
  channel: DsrChannel
  receivedOn: string
  dueOn: string
  /** The clock today (server-computed with `dsrClock`); frozen at the closing day once closed. */
  day: number
  daysLeft: number
  overdue: boolean
  closedAt: string | null
  subjectType: LegalHoldSubjectType | null
  subjectId: string | null
  subjectLabel: string | null
  details: string | null
  refusalReason: string | null
  assignee: { id: string; name: string } | null
  createdAt: string
  updatedAt: string
  version: number
}

export interface DsrRequestDetail extends DsrRequest {
  events: DsrEvent[]
}

/**
 * The refusal reason recorded when an erasure cannot complete because business records (issued
 * documents) still reference the subject (422 DSR_SUBJECT_REFERENCED). Stored as `en` then `ar` on two
 * lines, so the requester can be told in either language.
 */
export const DSR_REFERENCED_REFUSAL = {
  en: 'Kept for legal and tax records: referenced by issued documents',
  ar: 'محفوظة للسجلات القانونية والضريبية: تشير إليها مستندات صادرة',
} as const
export const DSR_REFERENCED_REFUSAL_TEXT = `${DSR_REFERENCED_REFUSAL.en}\n${DSR_REFERENCED_REFUSAL.ar}`

/** GET /privacy/dsr/:id/export: the subject's personal data from every adapter that holds it (JSON). */
export interface DsrExport {
  requestId: string
  kind: DsrKind
  subjectType: LegalHoldSubjectType
  subjectId: string
  generatedAt: string
  /** One entry per located subject reference, keyed by adapter. */
  records: { adapter: string; subjectRef: string; data: Record<string, unknown> }[]
}
